import React, { useState, useEffect, useMemo, Fragment } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  ChevronRight,
  Users,
  Store,
  Loader2,
  Search,
  CheckCircle2,
  Clock,
} from "lucide-react";
import { db } from "../services/firebase";
import {
  handleDispatchSellerPaymentWithJre,
  verifySellerPaymentHandover,
  resendSellerPaymentHandoverOtp,
  isSellerPaymentSettled,
  isSellerPaymentInFlight,
  isSellerPaymentHandedOver,
  PAYMENT_LEG_ORDER_STATUSES,
  shouldShowInOrderApproved,
} from "./services/paymentService";
import PaymentDispatchIndicator from "../components/PaymentDispatchIndicator";
import {
  calculateBuyerOrderTotals,
  calculateBuyerProductLine,
  calculateSellerOrderTotals,
  calculateSellerProductLine,
} from "../utils/orderSettlementCalculations";
import Layout from "../component/Layout";
import ProductImage from "../components/ProductImage";
import PayModal from "./components/PayModal";
import OtpVerificationModal from "./components/OtpVerificationModal";
/* ============================================================================
   DESIGN TOKENS
   ============================================================================ */
const c = {
  ink: "#0f172a",
  inkSoft: "#475569",
  inkFaint: "#94a3b8",
  border: "#e2e8f0",
  borderStrong: "#cbd5e1",
  surface: "#ffffff",
  canvas: "#f4f6f9",
  head: "#1e2536",
  headText: "#e2e8f0",
  amber: "#b45309",
  amberSoft: "#fef3e2",
  indigo: "#4338ca",
  indigoSoft: "#eef2ff",
  green: "#15803d",
  greenSoft: "#dcfce7",
  red: "#b91c1c",
  redSoft: "#fee2e2",
};

const inject = `
  .oa-row:hover { background-color: #f1f5f9 !important; }
  .oa-order-row:hover { background-color: #eef2ff !important; }
  .oa-chevron { transition: transform 0.15s ease; }
  .oa-chevron.open { transform: rotate(90deg); }
  .oa-scroll-table thead th { position: sticky; top: 0; z-index: 1; }
  .oa-search:focus { outline: none; border-color: ${c.amber} !important; box-shadow: 0 0 0 3px ${c.amberSoft}; }
  .oa-switch:focus-visible { outline: 2px solid ${c.amber}; outline-offset: 2px; }
  @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

/* ============================================================================
   FORMATTING HELPERS
   ============================================================================ */
const formatDate = (date) => (date ? date.toLocaleString("en-IN") : "—");

const getAge = (orderTime) => {
  if (!orderTime) return "—";
  const diffMs = new Date() - orderTime;
  const days = Math.floor(diffMs / 86400000);
  const hrs = Math.floor((diffMs % 86400000) / 3600000);
  const mins = Math.floor((diffMs % 3600000) / 60000);
  const secs = Math.floor((diffMs % 60000) / 1000);
  let parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  parts.push(`${secs}s`);
  return parts.join(" ");
};

const inr = (n) =>
  `₹${Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/* ============================================================================
   PRIMITIVES
   ============================================================================ */
const StatusPill = ({ confirmed }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      padding: "3px 10px",
      borderRadius: "999px",
      fontSize: "12px",
      fontWeight: 600,
      backgroundColor: confirmed ? c.greenSoft : c.redSoft,
      color: confirmed ? c.green : c.red,
    }}
  >
    {confirmed ? <CheckCircle2 size={13} /> : <Clock size={13} />}
    {confirmed ? "Confirmed" : "Pending"}
  </span>
);

const Chevron = ({ open }) => (
  <ChevronRight
    size={16}
    className={`oa-chevron${open ? " open" : ""}`}
    color={c.inkSoft}
  />
);

const ConfirmSwitch = ({ order, field, onToggle }) => {
  const isPrePickup = order.paymentPreference === "PRE_PICKUP";
  const isPaid =
    order.isSellerPaid === true ||
    order.sellerPaymentVerified === true ||
    order.sellerPaymentStatus === "verified" ||
    order.earlySellerPaymentCompleted === true;
  const disabled = field === "buyerConfirmation" && isPrePickup && !isPaid;
  const checked = !!order[field];

  return (
    <label
      className={`oa-switch ${disabled ? "disabled" : ""}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        position: "relative",
        opacity: disabled ? 0.5 : 1,
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled) e.preventDefault();
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={() => {
          if (!disabled) onToggle(order, field);
        }}
        style={{ position: "absolute", opacity: 0, width: 0, height: 0 }}
      />
      <span
        style={{
          width: "38px",
          height: "20px",
          backgroundColor: checked ? c.green : c.borderStrong,
          borderRadius: "999px",
          position: "relative",
          transition: "background-color 0.15s ease",
          display: "inline-block",
        }}
      >
        <span
          style={{
            position: "absolute",
            top: "2px",
            left: checked ? "20px" : "2px",
            width: "16px",
            height: "16px",
            backgroundColor: "#fff",
            borderRadius: "50%",
            transition: "left 0.15s ease",
            boxShadow: "0 1px 2px rgba(0,0,0,0.25)",
          }}
        />
      </span>
    </label>
  );
};

/* ============================================================================
   LEVEL 3 — SIZE ROWS  (compact; columns aligned with order-level table)
   Column mapping vs order table:
     blank(72px)=chevron+img | Size=product | Qty | Gross | Net | Spec | Fine
     | MC Total(+rate) | Stamp Total(+formula) | Amount | trailing-blank
   ============================================================================ */
const SizeRows = ({ order, showConfirmColumn = true, party = "buyer" }) => {
  if (!order.orderDetail) return null;
  const entries = Object.entries(order.orderDetail);
  if (entries.length === 0) return null;

  const thS = {
    border: "none",
    padding: "4px 8px",
    fontSize: "10px",
    fontWeight: 700,
    color: c.inkSoft,
    textAlign: "left",
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${c.border}`,
    lineHeight: "1.3",
    backgroundColor: "#eef0fa",
  };
  const tdS = {
    border: "none",
    padding: "4px 8px",
    fontSize: "11px",
    color: c.ink,
    borderBottom: `1px solid #e8ebf4`,
    whiteSpace: "nowrap",
    lineHeight: "1.3",
  };

  return (
    <tr>
      <td
        colSpan={showConfirmColumn ? 14 : 13}
        style={{ padding: 0, border: "none" }}
      >
        {/* colgroup widths mirror the parent order table exactly for alignment */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "72px" }} />
            {/* blank: chevron(28)+img(44) */}
            <col />
            {/* Size — flex (same as product col) */}
            <col style={{ width: "56px" }} />
            {/* Qty */}
            <col style={{ width: "82px" }} />
            {/* Gross Wt */}
            <col style={{ width: "76px" }} />
            {/* Net Wt */}
            <col style={{ width: "76px" }} />
            {/* Spec Wt */}
            <col style={{ width: "76px" }} />
            {/* Fine Wt */}
            <col style={{ width: "88px" }} />
            {/* MC Total (embeds ₹/g rate) */}
            <col style={{ width: "130px" }} />
            {/* Stamp Total (embeds ₹/set + formula) */}
            <col style={{ width: "90px" }} />
            {/* Amount */}
            <col />
            {/* trailing blank = time+age+confirm area */}
          </colgroup>
          <thead>
            <tr>
              <th style={thS} />
              <th style={thS}>Size</th>
              <th style={{ ...thS, textAlign: "right" }}>Qty</th>
              <th style={{ ...thS, textAlign: "right" }}>Gross Wt (g)</th>
              <th style={{ ...thS, textAlign: "right" }}>Net Wt (g)</th>
              <th style={{ ...thS, textAlign: "right" }}>Spec Wt (g)</th>
              <th style={{ ...thS, textAlign: "right" }}>Fine Wt (g)</th>
              <th style={{ ...thS, textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "9px",
                    color: c.inkFaint,
                    fontWeight: 600,
                  }}
                >
                  MC (₹/g)
                </div>
                <div>MC Total</div>
              </th>
              <th style={{ ...thS, textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "9px",
                    color: c.inkFaint,
                    fontWeight: 600,
                  }}
                >
                  Stamp (₹/set)
                </div>
                <div>Stamp Total</div>
              </th>
              <th style={{ ...thS, textAlign: "right" }}>Amount</th>
              <th style={thS} />
            </tr>
          </thead>
          <tbody>
            {entries.map(([size, variant]) => {
              const amt =
                party === "seller"
                  ? calculateSellerProductLine(order, variant)
                  : calculateBuyerProductLine(order, variant);
              const mcTotal = amt.setMcTotal + amt.gramTotal + amt.specTotal;
              const grossTotal = amt.gross;
              const netTotal = amt.net;
              const specTotal = amt.spec;
              return (
                <tr
                  key={`${order.docId}-${size}`}
                  style={{ backgroundColor: "#f7f8ff" }}
                >
                  <td style={tdS} />
                  <td style={{ ...tdS, fontWeight: 700 }}>{size}</td>
                  <td style={{ ...tdS, textAlign: "right" }}>
                    {amt.qty || "—"}
                  </td>
                  <td style={{ ...tdS, textAlign: "right" }}>
                    {grossTotal ? grossTotal.toFixed(3) : "—"}
                  </td>
                  <td style={{ ...tdS, textAlign: "right" }}>
                    {netTotal ? netTotal.toFixed(3) : "—"}
                  </td>
                  <td style={{ ...tdS, textAlign: "right" }}>
                    {specTotal ? specTotal.toFixed(3) : "—"}
                  </td>
                  <td
                    style={{
                      ...tdS,
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {amt.fineWeight.toFixed(3)}
                  </td>
                  {/* MC Total cell — rate (₹/g) shown as small label above total */}
                  <td style={{ ...tdS, textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: "9.5px",
                        color: c.inkFaint,
                        lineHeight: "1.2",
                      }}
                    >
                      ₹{amt.mcPerGram.toFixed(2)}/g
                    </div>
                    <div
                      style={{
                        fontVariantNumeric: "tabular-nums",
                        fontWeight: 600,
                        fontSize: "11px",
                      }}
                    >
                      {inr(mcTotal)}
                    </div>
                  </td>
                  {/* Stamp Total cell — rate + qty×rate=total formula */}
                  <td style={{ ...tdS, textAlign: "right" }}>
                    <div
                      style={{
                        fontSize: "9.5px",
                        color: c.inkFaint,
                        lineHeight: "1.2",
                      }}
                    >
                      ₹{amt.stampCost.toFixed(2)}/set
                    </div>
                    <div
                      style={{
                        fontVariantNumeric: "tabular-nums",
                        fontSize: "9.5px",
                      }}
                    >
                      {amt.qty}&nbsp;×&nbsp;₹{amt.stampCost.toFixed(2)}
                      &nbsp;=&nbsp;{inr(amt.stampTotal)}
                    </div>
                  </td>
                  <td
                    style={{
                      ...tdS,
                      textAlign: "right",
                      fontWeight: 700,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {inr(amt.total)}
                  </td>
                  <td style={tdS} />
                </tr>
              );
            })}
          </tbody>
        </table>
      </td>
    </tr>
  );
};

/* ============================================================================
   LEVEL 2 — ORDER ROW
   ============================================================================ */
const OrderRow = ({
  order,
  confirmField,
  isOpen,
  onToggleOrder,
  onToggleConfirm,
  showConfirmColumn = true,
  party = "buyer",
}) => {
  const td = {
    padding: "8px 10px",
    fontSize: "13px",
    borderBottom: `1px solid ${c.border}`,
    color: c.ink,
  };
  const totals =
    party === "seller"
      ? calculateSellerOrderTotals(order)
      : calculateBuyerOrderTotals(order);
  return (
    <Fragment>
      <tr
        className="oa-order-row"
        onClick={() => onToggleOrder(order.docId)}
        style={{
          cursor: "pointer",
          backgroundColor: isOpen ? c.indigoSoft : "transparent",
        }}
      >
        <td style={{ ...td, textAlign: "center", width: "28px" }}>
          <Chevron open={isOpen} />
        </td>
        {/* Stop propagation so clicking the image opens the full-screen viewer
            instead of toggling the size breakdown. */}
        <td
          style={{ ...td, textAlign: "center", width: "48px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <ProductImage
            images={order.images}
            productName={order.productName}
            label={`${order.productName || "Product"} · ${order.docId?.slice(0, 8)}`}
            size={34}
          />
        </td>
        <td style={td}>
          <div style={{ fontWeight: 600 }}>{order.productName || "—"}</div>
          <div
            style={{ fontSize: "10.5px", color: c.inkFaint, fontWeight: 500 }}
          >
            {order.docId}
          </div>
        </td>
        <td style={{ ...td, textAlign: "right" }}>{totals.totalQty}</td>
        <td
          style={{
            ...td,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {totals.gross.toFixed(3)}
        </td>
        <td
          style={{
            ...td,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {totals.net.toFixed(3)}
        </td>
        <td
          style={{
            ...td,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {totals.spec.toFixed(3)}
        </td>
        <td
          style={{
            ...td,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {totals.fineWeight.toFixed(3)}
        </td>
        <td
          style={{
            ...td,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {inr(totals.totalMc)}
        </td>
        <td
          style={{
            ...td,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {inr(totals.totalStamp)}
        </td>
        <td
          style={{
            ...td,
            textAlign: "right",
            fontWeight: 700,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {inr(totals.totalAmount)}
        </td>
        <td style={{ ...td, color: c.inkSoft }}>
          {formatDate(order.orderTime)}
        </td>
        <td style={{ ...td, color: c.inkSoft }}>{getAge(order.orderTime)}</td>
        {showConfirmColumn && (
          <td style={{ ...td, textAlign: "center" }}>
            <ConfirmSwitch
              order={order}
              field={confirmField}
              onToggle={onToggleConfirm}
            />
          </td>
        )}
      </tr>
      {isOpen && (
        <SizeRows
          order={order}
          showConfirmColumn={showConfirmColumn}
          party={party}
        />
      )}
    </Fragment>
  );
};

/* ============================================================================
   LEVEL 1 — GROUP TABLE (Buyer-wise or Seller-wise)
   ============================================================================ */
const GroupAccordionTable = ({
  icon: HeaderIcon,
  title,
  groups,
  idLabel,
  confirmField,
  confirmedFlagKey,
  expandedGroups,
  onToggleGroup,
  expandedOrders,
  onToggleOrder,
  onToggleConfirm,
  emptyMessage,
  accent,
  searchTerm,
  searchMatchesGroupId = false,
  onPayGroup,
  onResumePayment,
  verifyingGroupId,
  showConfirmColumn = true,
  party = "buyer",
}) => {
  const filtered = useMemo(() => {
    if (!searchTerm) return groups;
    const t = searchTerm.toLowerCase().trim();
    return groups.filter((g) => {
      const groupIdMatch = g.groupId.toLowerCase().includes(t);
      const orderMatch = g.orders.some((o) => {
        return (
          (o.docId && o.docId.toLowerCase().includes(t)) ||
          (o.sellerId && o.sellerId.toLowerCase().includes(t)) ||
          (o.buyerId && o.buyerId.toLowerCase().includes(t)) ||
          (o.productName && o.productName.toLowerCase().includes(t))
        );
      });
      return groupIdMatch || orderMatch;
    });
  }, [groups, searchTerm]);

  const th = {
    border: "none",
    padding: "10px 14px",
    fontSize: "12px",
    fontWeight: 700,
    textAlign: "left",
    color: c.headText,
    whiteSpace: "nowrap",
  };
  const td = {
    padding: "12px 14px",
    fontSize: "13.5px",
    borderBottom: `1px solid ${c.border}`,
    color: c.ink,
  };

  return (
    <div style={{ marginBottom: "28px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            width: "26px",
            height: "26px",
            borderRadius: "6px",
            backgroundColor: accent.bg,
            color: accent.fg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <HeaderIcon size={14} />
        </div>
        <h3
          style={{ margin: 0, fontSize: "15px", color: c.ink, fontWeight: 700 }}
        >
          {title}
        </h3>
        <span style={{ fontSize: "12px", color: c.inkFaint, fontWeight: 500 }}>
          {filtered.length} {filtered.length === 1 ? "group" : "groups"}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            color: c.inkFaint,
            padding: "28px 0",
            fontSize: "14px",
            backgroundColor: c.surface,
            border: `1px dashed ${c.border}`,
            borderRadius: "10px",
          }}
        >
          {emptyMessage || "Nothing to show here."}
        </div>
      ) : (
        <div
          className="oa-scroll-table"
          style={{
            border: `1px solid ${c.border}`,
            borderRadius: "10px",
            overflow: "hidden",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ backgroundColor: c.head }}>
                <th style={{ ...th, width: "5%" }} />
                <th style={th}>{idLabel}</th>
                <th style={{ ...th, textAlign: "right" }}>Orders</th>
                <th style={{ ...th, textAlign: "right" }}>Gross Wt (g)</th>
                <th style={{ ...th, textAlign: "right" }}>Net Wt (g)</th>
                <th style={{ ...th, textAlign: "right" }}>Spec Wt (g)</th>
                <th style={{ ...th, textAlign: "right" }}>Fine Wt (g)</th>
                <th style={{ ...th, textAlign: "right" }}>MC Total</th>
                <th style={{ ...th, textAlign: "right" }}>Stamp Total</th>
                <th style={{ ...th, textAlign: "right" }}>Amount</th>
                <th style={th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((group) => {
                const groupKey = `${idLabel}-${group.groupId}`;
                const isGroupOpen = !!expandedGroups[groupKey];
                return (
                  <Fragment key={groupKey}>
                    <tr
                      className="oa-row"
                      onClick={() => onToggleGroup(groupKey)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: isGroupOpen ? "#f8fafc" : c.surface,
                      }}
                    >
                      <td style={{ ...td, textAlign: "center" }}>
                        <Chevron open={isGroupOpen} />
                      </td>
                      <td style={{ ...td, fontWeight: 700 }}>
                        {group.groupId}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        {group.totalOrders}
                      </td>
                      <td
                        style={{
                          ...td,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {group.totalGrossWt}
                      </td>
                      <td
                        style={{
                          ...td,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {group.totalNetWt}
                      </td>
                      <td
                        style={{
                          ...td,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {group.totalSpecWt}
                      </td>
                      <td
                        style={{
                          ...td,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {group.totalFineWt}
                      </td>
                      <td
                        style={{
                          ...td,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {inr(group.totalMc)}
                      </td>
                      <td
                        style={{
                          ...td,
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {inr(group.totalStamp)}
                      </td>
                      <td
                        style={{
                          ...td,
                          textAlign: "right",
                          fontWeight: 700,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {inr(group.totalAmount)}
                      </td>
                      <td style={td}>
                        {confirmField === "sellerConfirmation" ? (
                          group.isPaid ? (
                            <StatusPill confirmed={true} />
                          ) : group.paymentHandedOver ? (
                            // Handover already verified: the JRE holds the money
                            // and is delivering it to the seller. Nothing left for
                            // the operator to do here, and re-running the handover
                            // OTP would be rejected.
                            <span
                              title="Payment handed over to the JRE — awaiting seller confirmation"
                              style={{
                                padding: "4px 10px",
                                backgroundColor: "#dcfce7",
                                color: "#166534",
                                border: "1px solid #86efac",
                                borderRadius: "12px",
                                fontWeight: "bold",
                                fontSize: "12px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              Handed Over
                            </span>
                          ) : group.paymentInFlight ? (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              <span
                                title="Seller payment assigned to a JRE — awaiting handover verification"
                                style={{
                                  padding: "4px 10px",
                                  backgroundColor: "#e0e7ff",
                                  color: "#4338ca",
                                  border: "1px solid #a5b4fc",
                                  borderRadius: "12px",
                                  fontWeight: "bold",
                                  fontSize: "12px",
                                }}
                              >
                                Assigned
                              </span>
                              <button
                                title="Send handover OTP to the JRE and confirm handover"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onResumePayment && onResumePayment(group);
                                }}
                                disabled={verifyingGroupId === group.groupId}
                                style={{
                                  padding: "4px 10px",
                                  backgroundColor: "#4338ca",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  cursor: verifyingGroupId === group.groupId ? "not-allowed" : "pointer",
                                  opacity: verifyingGroupId === group.groupId ? 0.7 : 1,
                                  fontWeight: "bold",
                                  fontSize: "12px",
                                }}
                              >
                                {verifyingGroupId === group.groupId ? "Sending OTP..." : "Verify JRE"}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onPayGroup && onPayGroup(group);
                              }}
                              style={{
                                padding: "4px 10px",
                                backgroundColor: "#f59e0b",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontWeight: "bold",
                                fontSize: "12px",
                              }}
                            >
                              Pay
                            </button>
                          )
                        ) : (
                          <StatusPill confirmed={group[confirmedFlagKey]} />
                        )}
                      </td>
                    </tr>

                    {isGroupOpen && (
                      <tr>
                        <td colSpan={11} style={{ padding: 0, border: "none" }}>
                          <div
                            style={{
                              backgroundColor: c.canvas,
                              padding: "16px",
                            }}
                          >
                            {/* Payment Dispatch Indicator */}
                            {party === "seller" && (
                              <PaymentDispatchIndicator
                                orders={group.orders}
                                stage="PRE_PICKUP"
                              />
                            )}

                            <div
                              style={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: "8px",
                                marginBottom: "8px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "12.5px",
                                  fontWeight: 700,
                                  color: c.inkSoft,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.03em",
                                }}
                              >
                                Orders
                              </span>
                              <span
                                style={{ fontSize: "12px", color: c.inkFaint }}
                              >
                                {group.orders.length} items
                              </span>
                            </div>
                            <div
                              style={{
                                backgroundColor: c.surface,
                                border: `1px solid ${c.border}`,
                                borderRadius: "8px",
                                overflow: "hidden",
                              }}
                            >
                              {/* tableLayout:fixed + colgroup so size sub-table columns align below */}
                              <table
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                  tableLayout: "fixed",
                                }}
                              >
                                <colgroup>
                                  <col style={{ width: "28px" }} />
                                  {/* chevron */}
                                  <col style={{ width: "44px" }} />
                                  {/* img */}
                                  <col />
                                  {/* product — flex */}
                                  <col style={{ width: "56px" }} />
                                  {/* qty */}
                                  <col style={{ width: "82px" }} />
                                  {/* gross */}
                                  <col style={{ width: "76px" }} />
                                  {/* net */}
                                  <col style={{ width: "76px" }} />
                                  {/* spec */}
                                  <col style={{ width: "76px" }} />
                                  {/* fine */}
                                  <col style={{ width: "88px" }} />
                                  {/* mc total */}
                                  <col style={{ width: "130px" }} />
                                  {/* stamp total */}
                                  <col style={{ width: "90px" }} />
                                  {/* amount */}
                                  <col style={{ width: "112px" }} />
                                  {/* order time */}
                                  <col style={{ width: "60px" }} />
                                  {/* age */}
                                  {showConfirmColumn && (
                                    <col style={{ width: "65px" }} />
                                  )}
                                </colgroup>
                                <thead>
                                  <tr style={{ backgroundColor: "#e2e8f0" }}>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                      }}
                                    />
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                      }}
                                    >
                                      Img
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                        textAlign: "left",
                                      }}
                                    >
                                      Product
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                        textAlign: "right",
                                      }}
                                    >
                                      Qty
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                        textAlign: "right",
                                      }}
                                    >
                                      Gross Wt (g)
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                        textAlign: "right",
                                      }}
                                    >
                                      Net Wt (g)
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                        textAlign: "right",
                                      }}
                                    >
                                      Spec Wt (g)
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                        textAlign: "right",
                                      }}
                                    >
                                      Fine Wt (g)
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                        textAlign: "right",
                                      }}
                                    >
                                      MC Total
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                        textAlign: "right",
                                      }}
                                    >
                                      Stamp Total
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                        textAlign: "right",
                                      }}
                                    >
                                      Amount
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                        textAlign: "left",
                                      }}
                                    >
                                      Order Time
                                    </th>
                                    <th
                                      style={{
                                        padding: "8px 10px",
                                        fontSize: "11px",
                                        fontWeight: 700,
                                        color: c.ink,
                                        textAlign: "left",
                                      }}
                                    >
                                      Age
                                    </th>
                                    {showConfirmColumn && (
                                      <th
                                        style={{
                                          padding: "8px 10px",
                                          fontSize: "11px",
                                          fontWeight: 700,
                                          color: c.ink,
                                          textAlign: "center",
                                        }}
                                      >
                                        Confirm
                                      </th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {group.orders.map((order) => (
                                    <OrderRow
                                      key={order.docId}
                                      order={order}
                                      confirmField={confirmField}
                                      isOpen={!!expandedOrders[order.docId]}
                                      onToggleOrder={onToggleOrder}
                                      onToggleConfirm={onToggleConfirm}
                                      showConfirmColumn={showConfirmColumn}
                                      party={party}
                                    />
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ============================================================================
   MAIN COMPONENT
   ============================================================================ */
const OrderApproved = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedOrders, setExpandedOrders] = useState({});
  const [search, setSearch] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedPayGroup, setSelectedPayGroup] = useState(null);
  const [handoverOtp, setHandoverOtp] = useState(null);
  const [verifyingGroupId, setVerifyingGroupId] = useState(null);

  const operatorId = localStorage.getItem("operatorId");

  useEffect(() => {
    if (!operatorId) return;
    const fetchOrders = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const orderListRef = collection(db, "orderList");

        // A PRE_PICKUP order is parked on 'paymentDispatchedToSeller' while the
        // JRE carries the money to the seller, so an RTD-only listing made it
        // disappear from this page for the whole payment. It returns to 'RTD'
        // once the seller confirms receipt (payment passed).
        //
        // Two separate queries on purpose: combining `orderStatus in [...]` with
        // `operatorId ==` needs a composite index, and if that index is missing
        // the whole page throws and renders nothing. The RTD query keeps its
        // original shape (index already proven to work) and the payment-leg
        // query stays single-field (always auto-indexed), so this page can never
        // be blanked out by a missing index.
        const seen = new Set();
        const ordersData = [];

        const pushOrders = (snap, { requireOperatorMatch = false } = {}) => {
          snap.forEach((docSnap) => {
            if (seen.has(docSnap.id)) return;
            const data = docSnap.data();
            if (requireOperatorMatch && data.operatorId !== operatorId) return;
            if (!shouldShowInOrderApproved(data)) return;
            seen.add(docSnap.id);
            let orderTime = null;
            if (data.orderTime?.seconds) {
              orderTime = new Date(data.orderTime.seconds * 1000);
            } else if (data.addedAt) {
              orderTime = new Date(data.addedAt);
            }
            ordersData.push({ docId: docSnap.id, ...data, orderTime });
          });
        };

        // 1. RTD orders for this operator (unchanged query shape).
        const rtdSnap = await getDocs(
          query(
            orderListRef,
            where("orderStatus", "==", "RTD"),
            where("operatorId", "==", operatorId),
          ),
        );
        pushOrders(rtdSnap);

        // 2. In-flight PRE_PICKUP payment-leg orders for this operator.
        // Wrapped so a failure here can never hide the RTD orders above.
        try {
          const legSnap = await getDocs(
            query(
              orderListRef,
              where("orderStatus", "in", PAYMENT_LEG_ORDER_STATUSES),
            ),
          );
          pushOrders(legSnap, { requireOperatorMatch: true });
        } catch (legErr) {
          console.warn("Could not load payment-leg orders:", legErr);
        }

        ordersData.sort((a, b) => (a.orderTime || 0) - (b.orderTime || 0));
        setOrders(ordersData);
      } catch (error) {
        console.error("Error fetching orders:", error);
        setLoadError(error.message || "Failed to load orders. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [operatorId, refreshKey]);

// Trigger handover OTP and open the verification modal for a seller payment
// assigned to a JRE.
const resumeHandoverOtp = async (group) => {
  setVerifyingGroupId(group.groupId);
  try {
    const data = await resendSellerPaymentHandoverOtp({
      ...group,
      jreId: group.orders[0]?.sellerPaymentJreId || group.orders[0]?.jreId,
    });
    setHandoverOtp(data);
  } catch (error) {
    console.error(error);
    alert(`Could not send handover OTP: ${error.message}`);
  } finally {
    setVerifyingGroupId(null);
  }
};

  const onPayModalConfirm = async (settlementPayload) => {
    try {
      const result = await handleDispatchSellerPaymentWithJre(
        selectedPayGroup.groupId,
        selectedPayGroup,
        (msg) => console.log(msg),
        settlementPayload,
        { stage: "PRE_PICKUP", skipOtp: true },
      );
      setSelectedPayGroup(null);
      setRefreshKey((k) => k + 1);
      // Assign step complete — the seller row now shows "Assigned" with a
      // "Verify JRE" button. Handover OTP is NOT issued yet; the operator
      // triggers it when clicking "Verify JRE".
      alert(
        `Payment assigned to JRE ${result.jreId}. Click "Verify JRE" to send OTP and confirm handover.`,
      );
    } catch (error) {
      console.error(error);
      alert(`Payment failed: ${error.message}`);
    }
  };

  const buyerGroups = useMemo(() => {
    const groups = {};
    orders.forEach((order) => {
      const { buyerId } = order;
      if (!buyerId) return;
      const {
        fineWeight: fineWt,
        totalAmount,
        totalMc,
        totalStamp,
        gross,
        net,
        spec,
      } = calculateBuyerOrderTotals(order);

      if (!groups[buyerId]) {
        groups[buyerId] = {
          groupId: buyerId,
          totalOrders: 0,
          totalGrossWt: 0,
          totalNetWt: 0,
          totalSpecWt: 0,
          totalFineWt: 0,
          totalMc: 0,
          totalStamp: 0,
          totalAmount: 0,
          isConfirmed: true,
          orders: [],
        };
      }
      const g = groups[buyerId];
      g.totalOrders += 1;
      g.totalGrossWt += gross;
      g.totalNetWt += net;
      g.totalSpecWt += spec;
      g.totalFineWt += fineWt;
      g.totalMc += totalMc;
      g.totalStamp += totalStamp;
      g.totalAmount += totalAmount;
      g.orders.push(order);
      if (!order.buyerConfirmation) g.isConfirmed = false;
    });

    return Object.values(groups)
      .map((g) => ({
        ...g,
        totalGrossWt: g.totalGrossWt.toFixed(3),
        totalNetWt: g.totalNetWt.toFixed(3),
        totalSpecWt: g.totalSpecWt.toFixed(3),
        totalFineWt: g.totalFineWt.toFixed(3),
        totalMc: g.totalMc,
        totalStamp: g.totalStamp,
        totalAmount: g.totalAmount,
      }))
      .sort((a, b) => a.groupId.localeCompare(b.groupId));
  }, [orders]);

  const sellerGroups = useMemo(() => {
    const groups = {};
    orders.forEach((order) => {
      if (order.paymentPreference !== "PRE_PICKUP") return;
      const sellerPaid = isSellerPaymentSettled(order);
      if (sellerPaid) return;

      const sellerId = order.sellerId || "UNKNOWN_SELLER";
      const {
        fineWeight: fineWt,
        totalAmount,
        totalMc,
        totalStamp,
        gross,
        net,
        spec,
      } = calculateSellerOrderTotals(order);

      if (!groups[sellerId]) {
        groups[sellerId] = {
          groupId: sellerId,
          totalOrders: 0,
          totalGrossWt: 0,
          totalNetWt: 0,
          totalSpecWt: 0,
          totalFineWt: 0,
          totalMc: 0,
          totalStamp: 0,
          totalAmount: 0,
          orders: [],
          isPaid: true,
          paymentInFlight: false,
          paymentHandedOver: false,
          allConfirmed: true,
        };
      }
      const g = groups[sellerId];
      g.totalOrders += 1;
      g.totalGrossWt += gross;
      g.totalNetWt += net;
      g.totalSpecWt += spec;
      g.totalFineWt += fineWt;
      g.totalMc += totalMc;
      g.totalStamp += totalStamp;
      g.totalAmount += totalAmount;
      g.orders.push(order);
      if (!order.sellerConfirmation) g.allConfirmed = false;
      if (!sellerPaid) g.isPaid = false;
      if (isSellerPaymentInFlight(order)) g.paymentInFlight = true;
      // Money is already with the JRE and travelling to the seller. The order is
      // visible here until the seller confirms receipt, but the operator must
      // NOT be offered the "Verify JRE" handover step again.
      if (isSellerPaymentHandedOver(order)) g.paymentHandedOver = true;
    });

    return Object.values(groups)
      .map((g) => ({
        ...g,
        totalGrossWt: g.totalGrossWt.toFixed(3),
        totalNetWt: g.totalNetWt.toFixed(3),
        totalSpecWt: g.totalSpecWt.toFixed(3),
        totalFineWt: g.totalFineWt.toFixed(3),
        totalMc: g.totalMc,
        totalStamp: g.totalStamp,
        totalAmount: g.totalAmount,
      }))
      .sort((a, b) => a.groupId.localeCompare(b.groupId));
  }, [orders]);

  const toggleGroup = (groupKey) =>
    setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));

  const toggleOrder = (docId) =>
    setExpandedOrders((prev) => ({ ...prev, [docId]: !prev[docId] }));

  const toggleConfirm = async (order, field) => {
    const orderDocRef = doc(collection(db, "orderList"), order.docId);
    const newStatus = !order[field];
    const confirmedAtField =
      field === "buyerConfirmation" ? "buyerConfirmedAt" : "sellerConfirmedAt";
    const confirmedAt = newStatus ? new Date() : null;

    try {
      await updateDoc(orderDocRef, {
        [field]: newStatus,
        [confirmedAtField]: confirmedAt,
      });
      setOrders((prev) =>
        prev.map((o) =>
          o.docId === order.docId
            ? { ...o, [field]: newStatus, [confirmedAtField]: confirmedAt }
            : o,
        ),
      );
    } catch (error) {
      console.error("Error toggling confirmation:", error);
      alert("Failed to update confirmation. Please check console for details.");
    }
  };

  return (
    <Layout pageTitle="Buyer Confirmation (RTD Orders)">
      <style>{inject}</style>
      <div
        style={{
          padding: "18px 20px",
          backgroundColor: c.canvas,
          minHeight: "100%",
        }}
      >
        {/* ---- Header ---- */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: "19px",
                fontWeight: 800,
                color: c.ink,
              }}
            >
              Ready to Dispatch — Confirmations
            </h2>
            <p
              style={{
                margin: "2px 0 0 0",
                fontSize: "13px",
                color: c.inkFaint,
              }}
            >
              Review buyer and seller confirmations, grouped and drilled down by
              order and size.
            </p>
          </div>
          <div style={{ position: "relative" }}>
            <Search
              size={15}
              style={{
                position: "absolute",
                left: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                color: c.inkFaint,
              }}
            />
            <input
              className="oa-search"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by Order ID, Seller ID, Buyer ID..."
              style={{
                padding: "8px 12px 8px 32px",
                fontSize: "13px",
                border: `1px solid ${c.border}`,
                borderRadius: "8px",
                width: "220px",
                backgroundColor: c.surface,
                transition: "box-shadow 0.15s ease, border-color 0.15s ease",
              }}
            />
          </div>
        </div>

        {/* ---- Loading / error states ---- */}
        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: c.inkFaint,
              fontSize: "14px",
              padding: "24px 0",
            }}
          >
            <Loader2
              size={16}
              style={{ animation: "spin 1s linear infinite" }}
            />
            Loading orders…
          </div>
        )}
        {loadError && (
          <div
            style={{
              backgroundColor: c.redSoft,
              color: c.red,
              padding: "10px 14px",
              borderRadius: "8px",
              fontSize: "13px",
              marginBottom: "16px",
            }}
          >
            {loadError}
          </div>
        )}

        {!loading && !loadError && (
          <>
            {sellerGroups.length > 0 && (
              <GroupAccordionTable
                icon={Store}
                title="Seller Summary — Pre-Pickup Orders"
                groups={sellerGroups}
                idLabel="Seller ID"
                confirmField="sellerConfirmation"
                confirmedFlagKey="allConfirmed"
                expandedGroups={expandedGroups}
                onToggleGroup={toggleGroup}
                expandedOrders={expandedOrders}
                onToggleOrder={toggleOrder}
                onToggleConfirm={toggleConfirm}
                emptyMessage="No Pre-Pickup orders right now."
                accent={{ bg: c.amberSoft, fg: c.amber }}
                searchTerm={search}
                searchMatchesGroupId
                onPayGroup={setSelectedPayGroup}
                onResumePayment={resumeHandoverOtp}
                verifyingGroupId={verifyingGroupId}
                showConfirmColumn={false}
                party="seller"
              />
            )}

            <GroupAccordionTable
              icon={Users}
              title="Buyer Summary — All RTD Orders"
              groups={buyerGroups}
              idLabel="Buyer ID"
              confirmField="buyerConfirmation"
              confirmedFlagKey="isConfirmed"
              expandedGroups={expandedGroups}
              onToggleGroup={toggleGroup}
              expandedOrders={expandedOrders}
              onToggleOrder={toggleOrder}
              onToggleConfirm={toggleConfirm}
              emptyMessage="No RTD orders awaiting confirmation."
              accent={{ bg: c.indigoSoft, fg: c.indigo }}
              searchTerm={search}
              party="buyer"
            />
          </>
        )}

        <p style={{ fontSize: "12px", color: c.inkFaint, marginTop: "4px" }}>
          Click a Seller/Buyer row to expand its orders. Click an order row to
          expand its size-wise breakdown.
        </p>
      </div>

      {selectedPayGroup && (
        <PayModal
          seller={selectedPayGroup.groupId}
          group={selectedPayGroup}
          onClose={() => setSelectedPayGroup(null)}
          onConfirm={onPayModalConfirm}
        />
      )}

      {handoverOtp && (
        <OtpVerificationModal
          modalData={handoverOtp}
          onClose={() => setHandoverOtp(null)}
          onVerify={(msg) => {
            alert(msg);
            setRefreshKey((k) => k + 1);
          }}
          verifyFn={verifySellerPaymentHandover}
          resendFn={resendSellerPaymentHandoverOtp}
        />
      )}
    </Layout>
  );
};

export default OrderApproved;
